import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";

// Areas technicians can't use always ask for the PIN of someone allowed in (unlocks that one click only).
const OPEN_TO_ALL = new Set(["search", "dashboard", "reminders", "status_searches", "purchase_orders", "who_bought", "runsheet", "timesheets", "suppliers_contacts", "suppliers_folders"]);
export const isRestricted = (perm) => !!perm && !OPEN_TO_ALL.has(perm);

const Ctx = createContext(null), ChangeCtx = createContext(null);
export const usePin = () => useContext(Ctx);
export const useChangePin = () => useContext(ChangeCtx);

function ChangePin({ onClose }) {
  const [f, setF] = useState({ current_pin: "", new_pin: "", again: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value.replace(/\D/g, "").slice(0, 8) }));
  const save = async () => {
    if (f.new_pin !== f.again) { toast.error("The new PINs don't match"); return; }
    try { await api("PUT", "/auth/my-pin", { current_pin: f.current_pin, new_pin: f.new_pin }); toast.success("Your PIN has been changed"); onClose(); } catch (e) { toast.error(e.message); }
  };
  const inp = (k, label) => (<label className="block"><span className="text-[12px] font-bold text-slate-600">{label}</span>
    <input type="password" inputMode="numeric" autoComplete="off" value={f[k]} onChange={set(k)} onKeyDown={(e) => k === "again" && e.key === "Enter" && save()} data-testid={`mypin-${k}`} className="mt-0.5 w-full h-9 border-2 border-slate-300 rounded-md px-3 tracking-[0.4em] text-center focus:border-[#1f4e86] outline-none" /></label>);
  return (
    <div className="fixed inset-0 z-[300] bg-black/50 grid place-items-center" data-testid="mypin-popup">
      <div className="w-[320px] bg-white rounded-md shadow-2xl border-2 border-[#1f4e86] overflow-hidden">
        <div className="bg-[#1f4e86] text-white px-4 py-2 font-black flex items-center gap-2"><KeyRound className="h-4 w-4" />My PIN</div>
        <div className="p-4 space-y-2.5">
          {inp("current_pin", "Current PIN")}{inp("new_pin", "New PIN (3–8 numbers)")}{inp("again", "New PIN again")}
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} data-testid="mypin-cancel" className="flex-1 h-9 rounded-md bg-slate-100 font-bold hover:bg-slate-200">Cancel</button>
            <button onClick={save} data-testid="mypin-save" className="flex-1 h-9 rounded-md bg-[#1f4e86] text-white font-black hover:bg-[#173d6a]">Save PIN</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PinProvider({ children }) {
  const [ask, setAsk] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const openChange = useCallback(() => setChanging(true), []);
  const done = useRef(null);
  const askPin = useCallback((perm, label) => new Promise((resolve) => { done.current = resolve; setPin(""); setAsk({ perm, label }); }), []);
  const finish = (ok) => { setAsk(null); setPin(""); done.current?.(ok); done.current = null; };
  const submit = async () => {
    if (!pin.trim() || busy) return;
    setBusy(true);
    try { const r = await api("POST", "/auth/verify-pin", { pin, perm: ask.perm }); toast.success(`Unlocked by ${r.name}`); finish(true); }
    catch { toast.error("Sorry, you do not have access to this area"); finish(false); }
    finally { setBusy(false); }
  };
  return (
    <Ctx.Provider value={askPin}><ChangeCtx.Provider value={openChange}>
      {children}
      {changing && <ChangePin onClose={() => setChanging(false)} />}
      {ask && (
        <div className="fixed inset-0 z-[300] bg-black/50 grid place-items-center" data-testid="pin-popup">
          <div className="w-[320px] bg-white rounded-md shadow-2xl border-2 border-[#1f4e86] overflow-hidden">
            <div className="bg-[#1f4e86] text-white px-4 py-2 font-black flex items-center gap-2"><KeyRound className="h-4 w-4" />Security PIN</div>
            <div className="p-4 space-y-3 text-[13px]">
              <div className="text-slate-600">Enter your PIN to open <b>{ask.label}</b>.</div>
              <input type="password" inputMode="numeric" autoFocus autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") finish(false); }} data-testid="pin-input"
                className="w-full h-11 border-2 border-slate-300 rounded-md px-3 text-center text-[22px] tracking-[0.5em] focus:border-[#1f4e86] outline-none" />
              <div className="flex gap-2">
                <button onClick={() => finish(false)} data-testid="pin-cancel" className="flex-1 h-9 rounded-md bg-slate-100 font-bold hover:bg-slate-200">Cancel</button>
                <button onClick={submit} disabled={busy || !pin} data-testid="pin-enter" className="flex-1 h-9 rounded-md bg-[#1f4e86] text-white font-black hover:bg-[#173d6a] disabled:opacity-50">Enter</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ChangeCtx.Provider></Ctx.Provider>
  );
}
